import type ArrayProxy from '@ember/array/proxy';
import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import { task, type TaskInstance } from 'ember-concurrency';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type FeatureFlagsService from 'garaje/services/feature-flags';

export default class BlocklistFilterService extends Service {
  @service declare store: Store;
  @service declare featureFlags: FeatureFlagsService;

  get locationBlocklistFilters(): ArrayProxy<BlacklistFilter> | BlacklistFilter[] {
    return this.queryByCompanyTask.lastSuccessful?.value || [];
  }

  get propertyBlocklistFilters(): ArrayProxy<BlacklistFilter> | BlacklistFilter[] {
    return this.queryByPropertyTask.lastSuccessful?.value || [];
  }

  // legacy API
  query(companyId: string): TaskInstance<ArrayProxy<BlacklistFilter>> {
    return this.queryByCompanyTask.perform(companyId);
  }

  // "This location" passes filter[applied-location] (the BE self-scopes the company, so it is omitted);
  // "All locations" passes filter[company]. The include is gated on the flag because develop's resource
  // exposes company/locations as filters, not relationships — an unconditional include would raise
  // InvalidInclude → 400 for every company.
  queryByCompanyTask = task(async (companyId: string, appliedLocationId?: string) => {
    const filter: Record<string, string> = appliedLocationId
      ? { 'applied-location': appliedLocationId }
      : { company: companyId };
    const query: { filter: Record<string, string>; include?: string } = { filter };
    if (this.featureFlags.isEnabled('feat-blocklist-location-scoping')) {
      query.include = 'company,locations';
    }
    return await this.store.query('blacklist-filter', query);
  });

  queryByPropertyTask = task(async (propertyId: string) => {
    return await this.store.query('blacklist-filter', {
      filter: {
        property: propertyId,
      },
    });
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    blocklistFilter: BlocklistFilterService;
  }
}
